import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import NotifyMeApi from "../Api/NotifyMeApi";
import ProductApi from "../Api/ProductApi";
import usePermissions from "../hook/usePermissions";
import { formatDate } from "../helper/DateHelper";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../Config/Index";
import TablePagination from "./TablePagination";
import StandardDatePicker from "./StandardDatePicker";
import { Modal, Button } from "react-bootstrap";

const NotifyMeListLayer = () => {
    const { hasPermission } = usePermissions();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [productOptions, setProductOptions] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productPage, setProductPage] = useState(0);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchProductOptions = async (search = "", pageNum = 0, append = false) => {
        setLoadingProducts(true);
        try {
            const limit = 200;
            const res = await ProductApi.getAll(pageNum, limit, search);
            if (res.status && res.response && res.response.data) {
                const fetchedData = res.response.data || [];
                const total = res.response.total || 0;
                const fetchedOpts = fetchedData.map(p => ({ value: p._id, label: p.name }));

                setHasMoreProducts((pageNum + 1) * limit < total && fetchedData.length === limit);

                setProductOptions(prev => {
                    const base = append ? [...prev, ...fetchedOpts] : fetchedOpts;
                    const map = new Map();
                    base.forEach(o => map.set(o.value, o));
                    if (selectedProducts && selectedProducts.length > 0) {
                        selectedProducts.forEach(sel => {
                            if (!map.has(sel.value)) {
                                map.set(sel.value, sel);
                            }
                        });
                    }
                    return Array.from(map.values());
                });
                setProductPage(pageNum);
            }
        } catch (error) {
            console.error("Error fetching products for dropdown:", error);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleMenuScrollToBottom = () => {
        if (hasMoreProducts && !loadingProducts) {
            fetchProductOptions(productSearch, productPage + 1, true);
        }
    };

    useEffect(() => {
        fetchProductOptions("", 0, false);
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchProductOptions(productSearch, 0, false);
        }, 300);
        return () => clearTimeout(timer);
    }, [productSearch]);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const params = {
                page,
                rowsPerPage,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
                productId: selectedProducts && selectedProducts.length > 0
                    ? selectedProducts.map(p => p.value).join(',')
                    : undefined
            };
            const res = await NotifyMeApi.list(params);
            if (res.status) {
                setRequests(res.response.data.data || []);
                setTotalCount(res.response.data.total || 0);
            }
        } catch (error) {
            console.error("Error fetching notify requests:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = (id) => {
        setItemToDelete(id);
        setShowDeleteModal(true);
    };

    const confirmRemove = async () => {
        if (itemToDelete) {
            const res = await NotifyMeApi.remove(itemToDelete);
            if (res.status) {
                toast.success("Request removed successfully");
                fetchRequests();
            } else {
                toast.error("Failed to remove request");
            }
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    const handleClearFilters = () => {
        setStartDate("");
        setEndDate("");
        setSelectedProducts([]);
        setProductSearch("");
        fetchProductOptions("", 0, false);
        setPage(0);
    };

    useEffect(() => {
        fetchRequests();
    }, [page, rowsPerPage, startDate, endDate, selectedProducts]);

    const getVariantImage = (item) => {
        if (!item.product) return null;

        // Try to find the specific variant image based on the requested combination
        if (item.combination && item.combination.length > 0 && item.product.attributes) {
            const variant = item.product.attributes.find(attr => {
                // Ensure the variant matches all values in the requested combination
                return attr.combination.length === item.combination.length &&
                    attr.combination.every(c1 =>
                        item.combination.some(c2 => c1.valueId === c2.valueId)
                    );
            });

            if (variant?.images?.[0]?.path) {
                return variant.images[0].path;
            }
        }

        // Fallback to main product image or the first attribute image if available
        return item.product.productImage?.path || item.product.attributes?.[0]?.images?.[0]?.path;
    };

    return (
        <div className="card h-100 p-0 radius-12 overflow-hidden shadow-sm border-0">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-20">
                    <h6 className="text-primary-600 pb-2 mb-0 d-flex align-items-center gap-2">
                        {/* <Icon icon="solar:bell-bing-bold-duotone" className="text-2xl" /> */}
                        Notify Me Requests
                    </h6>
                </div>

                <div className="row g-3 align-items-end">
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>From Date</label>
                        <StandardDatePicker
                            name="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="form-control radius-8"
                        />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>To Date</label>
                        <StandardDatePicker
                            name="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="form-control radius-8"
                        />
                    </div>
                    <div className="col-xl col-md-3 col-sm-6">
                        <label className="form-label fw-bold" style={{ fontSize: '14px' }}>Product (Optional)</label>
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            options={productOptions}
                            value={selectedProducts}
                            onChange={setSelectedProducts}
                            onInputChange={(inputValue, { action }) => {
                                if (action === "input-change") {
                                    setProductSearch(inputValue);
                                } else if (action === "input-blur" || action === "menu-close") {
                                    setProductSearch("");
                                }
                            }}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            filterOption={() => true}
                            isLoading={loadingProducts}
                            placeholder="All Products"
                            styles={selectStyles()}
                            isClearable
                        />
                    </div>
                    <div className="col-xl-auto col-md-3 col-sm-6 d-flex align-items-end gap-2">
                        {/* <button
                            type="button"
                            onClick={() => { setPage(0); fetchRequests(); }}
                            className="btn btn-primary d-flex align-items-center gap-2 radius-8 h-40-px"
                            style={{ fontSize: '14px' }}
                        >
                            Filter
                        </button> */}
                        <button
                            type="button"
                            onClick={handleClearFilters}
                            className="btn btn-outline-danger d-flex align-items-center gap-2 radius-8 h-40-px text-nowrap"
                        >
                            <Icon icon="solar:filter-remove-bold-duotone" fontSize={20} /> Clear
                        </button>
                    </div>
                </div>
            </div>

            <div className="card-body p-24">
                <div className="table-responsive">
                    <table className="table bordered-table sm-table mb-0">
                        <thead>
                            <tr className="bg-neutral-50 ">
                                <th scope="col" className="text-center py-16" style={{ width: '80px', color: "black", fontWeight: '600' }}>S.No</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Customer Details</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Product Details</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Attributes</th>
                                <th scope="col" className="py-16" style={{ color: "black", fontWeight: '600' }}>Requested Date</th>
                                <th scope="col" className="text-center py-16" style={{ color: "black", fontWeight: '600' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-100">
                                        <div className="spinner-border text-primary-600" role="status"></div>
                                    </td>
                                </tr>
                            ) : requests.length > 0 ? (
                                requests.map((item, index) => (
                                    <tr key={item._id}>
                                        <td className="text-center text-sm fw-medium text-secondary-light">
                                            {page * rowsPerPage + index + 1}
                                        </td>
                                        <td>
                                            <div className="d-flex flex-column">
                                                {item.customer ? (
                                                    <>
                                                        <span className="text-sm fw-bold text-secondary-light">{item.customer.fullName}</span>
                                                        <span className="text-xs text-secondary-400 d-flex align-items-center gap-1">
                                                            <Icon icon="solar:phone-bold" className="text-xs" />
                                                            {item.customer.phoneNumber}
                                                        </span>
                                                    </>
                                                ) : item.email ? (
                                                    <span className="text-sm fw-bold text-secondary-light d-flex align-items-center gap-1">
                                                        <Icon icon="solar:letter-bold" className="text-xs" />
                                                        {item.email}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-secondary-400">Guest User ({item.guestId?.slice(-6)})</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                {getVariantImage(item) ? (
                                                    <img
                                                        src={`${IMAGE_BASE_URL}/${getVariantImage(item)}`}
                                                        alt={item.product?.name}
                                                        className="w-40-px h-40-px rounded-circle object-fit-cover"
                                                    />
                                                ) : (
                                                    <div className="w-40-px h-40-px rounded-circle bg-neutral-100 d-flex align-items-center justify-content-center">
                                                        <Icon icon="solar:gallery-bold" className="text-secondary-light" />
                                                    </div>
                                                )}
                                                <div className="d-flex flex-column">
                                                    <span className="text-sm fw-bold text-secondary-light">{item.product?.name || "N/A"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {item.combination && item.combination.length > 0 ? (
                                                    item.combination.map((comb, i) => (
                                                        <span key={i} className="badge bg-primary-100 text-primary-600 text-xxxxs">
                                                            {comb.value}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span className="text-secondary-400 text-xs">-</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-sm text-secondary-light fw-medium">{formatDate(item.createdAt)}</span>
                                        </td>
                                        <td className="text-center">
                                            {hasPermission("Notify Me", "delete") && (
                                                <button
                                                    onClick={() => handleRemove(item._id)}
                                                    className="bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle mx-auto transition-all border-0"
                                                    title="Remove Request"
                                                    style={{ width: '40px', height: '40px' }}
                                                >
                                                    <Icon icon="solar:trash-bin-trash-bold" className="text-xl" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-100 italic text-secondary-400">
                                        No notification requests found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {!loading && totalCount > 0 && (
                    <div className="mt-24">
                        <TablePagination
                            currentPage={page}
                            totalPages={Math.ceil(totalCount / rowsPerPage)}
                            onPageChange={(p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value));
                                setPage(0);
                            }}
                            totalRecords={totalCount}
                        />
                    </div>
                )}
            </div>
            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Body className="text-center p-40">
                    <div className="d-flex justify-content-center mb-16">
                        <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
                            <Icon icon="fluent:delete-24-regular" className="text-danger-600 text-xxl" />
                        </div>
                    </div>
                    <h5 className="mb-8">Are you sure?</h5>
                    <p className="text-secondary-light mb-32 text-md">
                        Are you sure you want to remove this notify request? This action cannot be undone.
                    </p>
                    <div className="d-flex justify-content-center gap-3">
                        <Button variant="outline-secondary" className="px-32 py-12 radius-8 fw-semibold" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                        <Button variant="danger" className="px-32 py-12 radius-8 fw-semibold" onClick={confirmRemove}>Delete</Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default NotifyMeListLayer;
